import clsx from 'clsx';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { formatDateSafe } from '@/utils/dateUtils';
import { getEffectiveSegmentDates } from 'shared/vacations/effectiveSegments';
import {
    getSegmentTitle,
    resolveSegmentDecisionSource,
    resolveSegmentReviewState,
    sortSegmentsByDate,
} from '../../../utils/vacationSegments';
import { computeReturnDate } from '../../../utils/vacationRules';
import {
    buildReturnLabel,
    CLIENT_REVIEW_LABELS,
    CORRECTION_RETURNED_LABEL,
    NO_PERIODS_LABEL,
    PREVIOUS_DATES_LABEL,
    REJECTION_REASON_LABEL,
    SEGMENT_REVIEW_META,
} from '../vacationRequestSummary.definitions';
import type {
    SegmentDecision,
    VacationRequest,
    VacationRequestStatus,
    VacationSegment,
} from '../../../models/vacation.types';

interface SummaryPeriodsProps {
    segments: VacationRequest['segments'];
    dpSegmentDecisions?: Record<string, SegmentDecision> | null;
    clientSegmentDecisions?: Record<string, SegmentDecision> | null;
    requestStatus?: VacationRequestStatus;
    correctionReturnNote?: string | null;
    renderAction?: (segment: VacationSegment, decision: SegmentDecision | undefined) => React.ReactNode;
    actionPlacement?: 'divider' | 'plain';
}

export function SummaryPeriods({
    segments,
    dpSegmentDecisions,
    clientSegmentDecisions,
    requestStatus,
    correctionReturnNote,
    renderAction,
    actionPlacement = 'divider',
}: SummaryPeriodsProps) {
    if (segments.length === 0) {
        return <p className="text-base text-raro-gray-500 font-montserrat">{NO_PERIODS_LABEL}</p>;
    }

    const hasDecisions = Boolean(dpSegmentDecisions || clientSegmentDecisions);

    return (
        <ul className={clsx('grid gap-3', segments.length > 1 && 'sm:grid-cols-2')}>
            {sortSegmentsByDate(segments).map((segment) => {
                const resolved = resolveSegmentDecisionSource({ dpSegmentDecisions, clientSegmentDecisions }, segment.order);
                const decision = resolved?.decision;
                const state = resolveSegmentReviewState(decision, requestStatus);
                const clientLabel = resolved?.source === 'client' ? CLIENT_REVIEW_LABELS[state] : undefined;
                const meta = clientLabel
                    ? { ...SEGMENT_REVIEW_META[state], label: clientLabel }
                    : SEGMENT_REVIEW_META[state];
                const effectiveDates = getEffectiveSegmentDates(segment, decision);
                const returnDate = computeReturnDate(effectiveDates.startDate, segment.days);
                const isRejectedDecision = decision?.status === 'Rejected';
                const showPreviousDates =
                    isRejectedDecision &&
                    Boolean(decision?.startDate) &&
                    decision?.startDate !== segment.startDate;
                const showReturnNote = state === 'rejected' && Boolean(correctionReturnNote);

                return (
                    <li key={segment.order} className={clsx('flex flex-col rounded-xl p-4 ring-1', meta.cardClass)}>
                        <div className="flex flex-1 flex-col gap-3">
                            {hasDecisions && (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span
                                        className={clsx(
                                            'max-w-full rounded-full px-2.5 py-1 text-xs font-semibold font-montserrat',
                                            meta.badgeClass
                                        )}
                                    >
                                        {meta.label}
                                    </span>
                                    <span className="whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-700 font-montserrat">
                                        {segment.days} dias
                                    </span>
                                </div>
                            )}
                            {!hasDecisions && (
                                <div className="flex justify-end">
                                    <span className="whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-700 font-montserrat">
                                        {segment.days} dias
                                    </span>
                                </div>
                            )}

                            <div className="flex items-start gap-3">
                                <div className="shrink-0 rounded-lg bg-primary-100/60 p-2">
                                    <CalendarDaysIcon className="h-5 w-5 text-primary-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-raro-gray-600 font-montserrat">
                                        {getSegmentTitle(segment.position, segments.length)}
                                    </p>
                                    <p className="mt-0.5 text-lg font-semibold text-raro-gray-900 font-montserrat">
                                        {formatDateSafe(effectiveDates.startDate)} a {formatDateSafe(effectiveDates.endDate)}
                                    </p>
                                </div>
                            </div>

                            <p className="text-xs text-raro-gray-600 font-montserrat">
                                {buildReturnLabel(formatDateSafe(returnDate))}
                            </p>

                            {(showPreviousDates || (isRejectedDecision && decision?.note) || showReturnNote) && (
                                <div
                                    className={clsx(
                                        'space-y-1 border-t pt-2 text-xs font-montserrat',
                                        state === 'rejected'
                                            ? 'border-red-200 text-red-700'
                                            : 'border-amber-200 text-amber-800'
                                    )}
                                >
                                    {showPreviousDates && (
                                        <p>
                                            <strong>{PREVIOUS_DATES_LABEL}:</strong>{' '}
                                            {formatDateSafe(decision?.startDate)} a {formatDateSafe(decision?.endDate)}
                                        </p>
                                    )}
                                    {isRejectedDecision && decision?.note && (
                                        <p>
                                            <strong>{REJECTION_REASON_LABEL}:</strong> {decision.note}
                                        </p>
                                    )}
                                    {showReturnNote && (
                                        <p>
                                            <strong>{CORRECTION_RETURNED_LABEL}:</strong> {correctionReturnNote}
                                        </p>
                                    )}
                                </div>
                            )}

                            {renderAction && (
                                <div className={clsx('mt-auto', actionPlacement === 'divider' && 'border-t border-raro-gray-200/70 pt-3')}>
                                    {renderAction(segment, decision)}
                                </div>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

export default SummaryPeriods;
